import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { CalendarDays, Plus, Trash2, Video, Clapperboard, CircleCheck } from "lucide-react";
import { toast } from "sonner";

const CAP_LABELS = {
  camera: "Camera & Lenses",
  lighting: "Lighting & Generators",
  grip: "Grip / Cranes / Rigging",
  sound: "Location Sound",
  post: "DIT & Post",
};

/** Technical Vendor · Serialized inventory + 6-month availability grid. */
export default function VendorTechnicalPanel({ org }) {
  const [assets, setAssets] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [activeAsset, setActiveAsset] = useState(null);

  const refresh = async () => {
    const { data } = await api.get(`/orgs/${org.id}/vendor-assets`);
    setAssets(data.items || []);
  };
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  const removeAsset = async (id) => {
    if (!window.confirm("Remove this asset from your inventory?")) return;
    try {
      await api.delete(`/orgs/${org.id}/vendor-assets/${id}`);
      toast.success("Removed");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <section className="space-y-8" data-testid="vendor-technical-panel">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55 flex items-center gap-2">
            <Video size={11} className="text-[#F5B82E]" /> Technical Track · Serialized inventory
          </div>
          <h2 className="font-display text-2xl uppercase tracking-tight mt-1">Asset Inventory</h2>
          <div className="mt-1 text-xs text-white/50">{assets.length} assets across {(org.vendor_capabilities || []).length} capabilities</div>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] btn-themed"
              data-testid="add-asset-btn">
              <Plus size={12} className="mr-1.5" /> Add asset
            </Button>
          </DialogTrigger>
          <AddAssetDialog org={org} onClose={() => setAddOpen(false)} onAdded={refresh} />
        </Dialog>
      </div>

      {/* Asset list */}
      <div className="cs-card rounded-2xl overflow-hidden">
        <div className="grid grid-cols-[1fr_120px_100px_110px_60px] gap-3 px-5 py-3 border-b border-white/10 text-[10px] tracking-[0.22em] uppercase text-white/45">
          <div>Asset</div><div>Serial</div><div>Capability</div><div className="text-right">Rate/day</div><div />
        </div>
        {assets.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-white/50">
            No assets yet — add your first camera / lens / light / boom kit.
          </div>
        ) : assets.map((a) => (
          <div key={a.id}
               className="grid grid-cols-[1fr_120px_100px_110px_60px] gap-3 px-5 py-3 items-center border-b border-white/5 hover:bg-white/[0.02]"
               data-testid={`asset-${a.id}`}>
            <div>
              <button className="text-sm text-white text-left hover:text-[#F5B82E]"
                      onClick={() => setActiveAsset(a)}
                      data-testid={`asset-open-${a.id}`}>
                {a.name}
              </button>
              <div className="text-[10px] tracking-[0.18em] uppercase text-white/45 mt-0.5">
                {a.status === "available"
                  ? <><CircleCheck size={9} className="inline text-emerald-400 mr-0.5" /> Available</>
                  : a.status}
              </div>
            </div>
            <div className="font-mono text-xs text-white/70">{a.serial_no || "—"}</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-white/70">{CAP_LABELS[a.capability] || a.capability}</div>
            <div className="text-right text-sm text-white/85">₹{Number(a.daily_rate || 0).toLocaleString()}</div>
            <div className="text-right">
              <Button size="sm" variant="ghost"
                      onClick={() => removeAsset(a.id)}
                      className="h-8 w-8 p-0 text-white/45 hover:text-red-300 hover:bg-red-500/10"
                      data-testid={`asset-delete-${a.id}`}>
                <Trash2 size={13} />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {activeAsset && (
        <AssetCalendarDialog asset={activeAsset} orgId={org.id}
                             onClose={() => setActiveAsset(null)} />
      )}
    </section>
  );
}

function AddAssetDialog({ org, onClose, onAdded }) {
  const [name, setName] = useState("");
  const [serial, setSerial] = useState("");
  const [rate, setRate] = useState("");
  const [cap, setCap] = useState((org.vendor_capabilities || [])[0] || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/orgs/${org.id}/vendor-assets`, {
        name: name.trim(), serial_no: serial.trim(),
        capability: cap, daily_rate: Number(rate) || 0,
      });
      toast.success("Asset added");
      onAdded?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="bg-black border-white/15 text-white" data-testid="add-asset-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl uppercase tracking-tight">Add Serialized Asset</DialogTitle>
        <DialogDescription className="text-white/55">
          Assets sit on a 6-month calendar — track exactly what leaves the house and when it&rsquo;s back.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 mt-2">
        <div>
          <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="Arri Alexa Mini LF"
                 className="mt-1.5 bg-black/30 border-white/15"
                 data-testid="asset-name-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Serial #</Label>
            <Input value={serial} onChange={(e) => setSerial(e.target.value)}
                   placeholder="Optional"
                   className="mt-1.5 bg-black/30 border-white/15 font-mono"
                   data-testid="asset-serial-input" />
          </div>
          <div>
            <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Daily rate (₹)</Label>
            <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)}
                   className="mt-1.5 bg-black/30 border-white/15"
                   data-testid="asset-rate-input" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.22em] uppercase text-white/55">Capability</Label>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {(org.vendor_capabilities || []).map((c) => (
              <button key={c} onClick={() => setCap(c)}
                      data-testid={`asset-cap-${c}`}
                      className={`px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] border
                        ${cap === c
                          ? "border-[#F5B82E]/60 bg-[#F5B82E]/15 text-[#F5B82E]"
                          : "border-white/15 text-white/55 hover:border-white/35"}`}>
                {CAP_LABELS[c] || c}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-end pt-4">
        <Button onClick={submit} disabled={!name || !cap || busy}
                className="h-10 px-5 rounded-full text-[10px] uppercase tracking-[0.22em] btn-themed"
                data-testid="asset-submit">
          {busy ? "Adding…" : "Add Asset"}
        </Button>
      </div>
    </DialogContent>
  );
}

function AssetCalendarDialog({ asset, orgId, onClose }) {
  const [bookings, setBookings] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd]     = useState("");
  const [ref, setRef]     = useState("");
  const [busy, setBusy]   = useState(false);

  const refresh = async () => {
    const { data } = await api.get(`/orgs/${orgId}/vendor-assets/${asset.id}/bookings`);
    setBookings(data.items || []);
  };
  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  const book = async () => {
    setBusy(true);
    try {
      await api.post(`/orgs/${orgId}/vendor-assets/${asset.id}/bookings`, {
        start_date: start, end_date: end || start, project_ref: ref,
      });
      toast.success("Booked");
      setStart(""); setEnd(""); setRef("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Booking failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-black border-white/15 text-white max-w-2xl" data-testid="asset-calendar-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
            <Clapperboard size={18} className="text-[#F5B82E]" />
            {asset.name}
          </DialogTitle>
          <DialogDescription className="text-white/55 font-mono">
            {asset.serial_no || "—"} · {CAP_LABELS[asset.capability] || asset.capability} · ₹{Number(asset.daily_rate || 0).toLocaleString()}/day
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 flex items-center gap-2">
            <CalendarDays size={11} /> Bookings ({bookings.length})
          </div>
          <div className="mt-3 space-y-2 max-h-52 overflow-auto pr-1" data-testid="booking-list">
            {bookings.length === 0
              ? <div className="text-sm text-white/45">No bookings yet.</div>
              : bookings.map((b) => (
                <div key={b.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10">
                  <div className="text-xs text-white flex-1">
                    <div className="font-mono">{b.start_date} → {b.end_date}</div>
                    {b.project_ref && <div className="text-[10px] tracking-[0.18em] uppercase text-white/45">Ref: {b.project_ref}</div>}
                  </div>
                  <div className="text-[9px] tracking-[0.22em] uppercase text-emerald-300">{b.status}</div>
                </div>
              ))}
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-white/10">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/50 mb-2">New booking</div>
          <div className="grid grid-cols-[1fr_1fr_1.4fr_auto] gap-2">
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)}
                   className="bg-black/30 border-white/15 h-9 text-xs"
                   data-testid="book-start-input" />
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
                   className="bg-black/30 border-white/15 h-9 text-xs"
                   data-testid="book-end-input" />
            <Input value={ref} onChange={(e) => setRef(e.target.value)}
                   placeholder="Project ref (optional)"
                   className="bg-black/30 border-white/15 h-9 text-xs"
                   data-testid="book-ref-input" />
            <Button onClick={book} disabled={!start || busy}
                    className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.22em] btn-themed"
                    data-testid="book-submit">
              {busy ? "…" : "Reserve"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
